import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { getPostById, getPosts, getPostCategories } from '../services/blogService';
import { getBlogCategories } from '../services/blogCategoryService';
import { hasPermission } from '../services/authService';
import { resolveAssetUrl } from '../utils/resolveUrl';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [dbCategories, setDbCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const admin = hasPermission('admin.blogs');

  // Dynamic Promo Card State
  const [promoBadge, setPromoBadge] = useState(localStorage.getItem('blog_promo_badge') || 'GLOBE ONLINE');
  const [promoTitle, setPromoTitle] = useState(localStorage.getItem('blog_promo_title') || 'Zbuloni ofertat më të mira në teknologji');
  const [promoDescription, setPromoDescription] = useState(localStorage.getItem('blog_promo_description') || 'Cilësia më e lartë, garanci zyrtare dhe dërgesa të shpejta në të gjithë Shqipërinë.');
  const [promoBtnText, setPromoBtnText] = useState(localStorage.getItem('blog_promo_btn_text') || 'Eksploro Produktet →');
  const [promoBtnLink, setPromoBtnLink] = useState(localStorage.getItem('blog_promo_btn_link') || '/products');

  // Edit Promo Card Modal State
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [tempPromoBadge, setTempPromoBadge] = useState(promoBadge);
  const [tempPromoTitle, setTempPromoTitle] = useState(promoTitle);
  const [tempPromoDesc, setTempPromoDesc] = useState(promoDescription);
  const [tempPromoBtnText, setTempPromoBtnText] = useState(promoBtnText);
  const [tempPromoBtnLink, setTempPromoBtnLink] = useState(promoBtnLink);
  const [savingPromo, setSavingPromo] = useState(false);
  const [showAllSidebarCats, setShowAllSidebarCats] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);
    
    Promise.all([
      getPostById(id),
      getPosts().catch(() => []),
      getBlogCategories().catch(() => []),
      fetch(`${API_BASE_URL}/settings`).then(r => r.json()).catch(() => ({}))
    ])
      .then(([p, postsList, catsList, settings]) => {
        if (p?.error) setError(p.error);
        else setPost(p);
        setAllPosts(Array.isArray(postsList) ? postsList : []);
        setDbCategories(Array.isArray(catsList) ? catsList : []);

        if (settings) {
          if (settings.blog_promo_badge) {
            setPromoBadge(settings.blog_promo_badge);
            setTempPromoBadge(settings.blog_promo_badge);
            localStorage.setItem('blog_promo_badge', settings.blog_promo_badge);
          }
          if (settings.blog_promo_title) {
            setPromoTitle(settings.blog_promo_title);
            setTempPromoTitle(settings.blog_promo_title);
            localStorage.setItem('blog_promo_title', settings.blog_promo_title);
          }
          if (settings.blog_promo_description !== undefined) {
            setPromoDescription(settings.blog_promo_description);
            setTempPromoDesc(settings.blog_promo_description);
            localStorage.setItem('blog_promo_description', settings.blog_promo_description);
          }
          if (settings.blog_promo_btn_text) {
            setPromoBtnText(settings.blog_promo_btn_text);
            setTempPromoBtnText(settings.blog_promo_btn_text);
            localStorage.setItem('blog_promo_btn_text', settings.blog_promo_btn_text);
          }
          if (settings.blog_promo_btn_link) {
            setPromoBtnLink(settings.blog_promo_btn_link);
            setTempPromoBtnLink(settings.blog_promo_btn_link);
            localStorage.setItem('blog_promo_btn_link', settings.blog_promo_btn_link);
          }
        }
      })
      .catch(() => setError('Dështoi ngarkimi i artikullit'))
      .finally(() => setLoading(false));

    const handleStorage = () => {
      const b = localStorage.getItem('blog_promo_badge');
      const t = localStorage.getItem('blog_promo_title');
      const d = localStorage.getItem('blog_promo_description');
      const bt = localStorage.getItem('blog_promo_btn_text');
      const bl = localStorage.getItem('blog_promo_btn_link');
      if (b) { setPromoBadge(b); setTempPromoBadge(b); }
      if (t) { setPromoTitle(t); setTempPromoTitle(t); }
      if (d !== null) { setPromoDescription(d); setTempPromoDesc(d); }
      if (bt) { setPromoBtnText(bt); setTempPromoBtnText(bt); }
      if (bl) { setPromoBtnLink(bl); setTempPromoBtnLink(bl); }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [id]);

  const handleSavePromo = async (e) => {
    if (e) e.preventDefault();
    setSavingPromo(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          blog_promo_badge: tempPromoBadge,
          blog_promo_title: tempPromoTitle,
          blog_promo_description: tempPromoDesc,
          blog_promo_btn_text: tempPromoBtnText,
          blog_promo_btn_link: tempPromoBtnLink
        })
      });
      setPromoBadge(tempPromoBadge);
      setPromoTitle(tempPromoTitle);
      setPromoDescription(tempPromoDesc);
      setPromoBtnText(tempPromoBtnText);
      setPromoBtnLink(tempPromoBtnLink);
      localStorage.setItem('blog_promo_badge', tempPromoBadge);
      localStorage.setItem('blog_promo_title', tempPromoTitle);
      localStorage.setItem('blog_promo_description', tempPromoDesc);
      localStorage.setItem('blog_promo_btn_text', tempPromoBtnText);
      localStorage.setItem('blog_promo_btn_link', tempPromoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      toast.success('Karta reklamuese u përditësua me sukses!');
      setShowPromoModal(false);
    } catch (err) {
      setPromoBadge(tempPromoBadge);
      setPromoTitle(tempPromoTitle);
      setPromoDescription(tempPromoDesc);
      setPromoBtnText(tempPromoBtnText);
      setPromoBtnLink(tempPromoBtnLink);
      localStorage.setItem('blog_promo_badge', tempPromoBadge);
      localStorage.setItem('blog_promo_title', tempPromoTitle);
      localStorage.setItem('blog_promo_description', tempPromoDesc);
      localStorage.setItem('blog_promo_btn_text', tempPromoBtnText);
      localStorage.setItem('blog_promo_btn_link', tempPromoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      toast.success('Karta u ruajt lokalisht.');
      setShowPromoModal(false);
    } finally {
      setSavingPromo(false);
    }
  };

  const copyShareLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Linku u kopjua me sukses!');
    }
  };

  // Extract all categories for current post
  const postCategories = useMemo(() => {
    return getPostCategories(post);
  }, [post]);

  // Aggregate category counts across all posts
  const categoryCounts = useMemo(() => {
    const counts = {};
    allPosts.forEach(p => {
      getPostCategories(p).forEach(cat => {
        counts[cat] = (counts[cat] || 0) + 1;
      });
    });
    // Include any empty categories from database
    dbCategories.forEach(c => {
      if (c?.name && !counts[c.name]) {
        counts[c.name] = 0;
      }
    });
    return counts;
  }, [allPosts, dbCategories]);

  // Sorted list of unique categories
  const categoriesList = useMemo(() => {
    return Object.keys(categoryCounts).sort((a, b) => (categoryCounts[b] || 0) - (categoryCounts[a] || 0));
  }, [categoryCounts]);

  if (loading) {
    return (
      <div style={{ maxWidth: 1440, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, color: '#64748b', fontWeight: 500 }}>Po ngarkohet artikulli…</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ maxWidth: 1440, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 20, color: '#ef4444', marginBottom: 16, fontWeight: 600 }}>{error || 'Artikulli nuk u gjet'}</div>
        <Link to="/blog" style={{ color: '#007da5', textDecoration: 'none', fontWeight: 600, fontSize: 16 }}>
          ← Kthehu te të gjithë artikujt
        </Link>
      </div>
    );
  }

  const imgUrl = post.image ? resolveAssetUrl(post.image) : '';
  const dateStr = post.createdAt ? new Date(post.createdAt).toLocaleDateString('sq-AL', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const wordCount = (post.content || '').replace(/<[^>]+>/g, '').split(/\s+/).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  const otherPosts = allPosts.filter(p => String(p.id) !== String(id));
  const recentPosts = otherPosts.slice(0, 4);
  const bottomRelated = otherPosts.slice(0, 3);

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px 0 80px' }}>
      <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 18px', boxSizing: 'border-box' }}>
        
        {/* Top Breadcrumb Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#64748b', flexWrap: 'wrap' }}>
            <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Kryefaqja</Link>
            <span>/</span>
            <Link to="/blog" style={{ color: '#007da5', textDecoration: 'none', fontWeight: 600 }}>Blog</Link>
            {postCategories.length > 0 && (
              <>
                <span>/</span>
                {postCategories.map((cat, idx) => (
                  <React.Fragment key={cat}>
                    {idx > 0 && <span style={{ color: '#94a3b8' }}>,</span>}
                    <Link
                      to={`/blog?category=${encodeURIComponent(cat)}`}
                      style={{ color: '#007da5', textDecoration: 'none', fontWeight: 600 }}
                    >
                      {cat}
                    </Link>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          <Link
            to="/blog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: '#007da5',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              background: '#ffffff',
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}
          >
            ← Të gjithë artikujt
          </Link>
        </div>

        {/* 2-Column Wide Layout */}
        <div className="blog-post-grid">
          
          {/* Main Article Column */}
          <main className="blog-post-main">
            <article
              className="p-5 sm:p-8 md:p-10"
              style={{
                background: '#ffffff',
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 25px rgba(0,0,0,0.03)',
                overflow: 'hidden'
              }}
            >
              {/* Category Badges & Meta Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {postCategories.map(cat => (
                  <Link
                    key={cat}
                    to={`/blog?category=${encodeURIComponent(cat)}`}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: '#e0f2fe',
                      color: '#007da5',
                      padding: '4px 12px',
                      borderRadius: 6,
                      textDecoration: 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {cat}
                  </Link>
                ))}
                {dateStr && (
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginLeft: postCategories.length > 0 ? 4 : 0 }}>
                    {dateStr}
                  </span>
                )}
                <span style={{ fontSize: 13, color: '#94a3b8' }}>•</span>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  {readTime} min lexim
                </span>
              </div>

              {/* Title */}
              <h1
                style={{
                  fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.8px',
                  color: '#0f172a',
                  lineHeight: 1.25,
                  margin: '0 0 20px 0'
                }}
              >
                {post.title}
              </h1>

              {/* Excerpt Subtitle */}
              {post.excerpt && (
                <div
                  style={{
                    fontSize: '1.15rem',
                    lineHeight: 1.65,
                    color: '#475569',
                    marginBottom: 28,
                    borderLeft: '3.5px solid #007da5',
                    paddingLeft: 18,
                    background: '#f8fafc',
                    padding: '14px 18px',
                    borderRadius: '0 8px 8px 0'
                  }}
                >
                  {post.excerpt}
                </div>
              )}

              {/* Hero Image Banner */}
              {imgUrl ? (
                <div style={{ width: '100%', marginBottom: 36, borderRadius: 14, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', maxHeight: 520 }}>
                  <img
                    src={imgUrl}
                    alt={post.title}
                    style={{ width: '100%', height: 'auto', maxHeight: 520, objectFit: 'cover', display: 'block' }}
                  />
                </div>
              ) : null}

              {/* Article Content */}
              <div
                className="ql-editor blog-article-content"
                style={{
                  fontSize: '1.1rem',
                  lineHeight: 1.85,
                  color: '#334155',
                  padding: 0
                }}
                dangerouslySetInnerHTML={{ __html: post.content || '' }}
              />

              {/* Share & Footer Bar */}
              <div
                style={{
                  marginTop: 48,
                  paddingTop: 24,
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 16
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Ndaje artikullin:</span>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#007da5',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Kopjo Linkun
                  </button>
                </div>

                <Link
                  to="/blog"
                  style={{
                    color: '#007da5',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 14
                  }}
                >
                  ← Kthehu te të gjithë artikujt
                </Link>
              </div>
            </article>
          </main>

          {/* Sticky Sidebar Column */}
          <aside className="blog-post-sidebar">
            
            {/* Recent Articles Widget */}
            {recentPosts.length > 0 && (
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  padding: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                  marginBottom: 24
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 18, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                  Artikuj të Fundit
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {recentPosts.map(p => {
                    const pImg = p.image ? resolveAssetUrl(p.image) : 'https://picsum.photos/seed/blog' + p.id + '/200/200';
                    return (
                      <Link
                        key={p.id}
                        to={`/blog/${p.id}`}
                        style={{
                          display: 'flex',
                          gap: 12,
                          textDecoration: 'none',
                          color: 'inherit',
                          alignItems: 'center'
                        }}
                      >
                        <img
                          src={pImg}
                          alt={p.title}
                          style={{
                            width: 72,
                            height: 60,
                            borderRadius: 8,
                            objectFit: 'cover',
                            flexShrink: 0,
                            background: '#f1f5f9'
                          }}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#0f172a',
                              lineHeight: 1.35,
                              marginBottom: 4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {p.title}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Categories Widget */}
            {categoriesList.length > 0 && (
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  padding: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                  marginBottom: 24
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Kategoritë</span>
                  <Link to="/blog" style={{ fontSize: 12.5, color: '#007da5', textDecoration: 'none', fontWeight: 600 }}>
                    Të gjitha
                  </Link>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(showAllSidebarCats ? categoriesList : categoriesList.slice(0, 6)).map(cat => (
                    <Link
                      key={cat}
                      to={`/blog?category=${encodeURIComponent(cat)}`}
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        color: '#334155',
                        padding: '7px 14px',
                        borderRadius: 8,
                        textDecoration: 'none',
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        touchAction: 'manipulation'
                      }}
                    >
                      {cat}
                    </Link>
                  ))}
                  {categoriesList.length > 6 && (
                    <button
                      type="button"
                      onClick={() => setShowAllSidebarCats(s => !s)}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        background: showAllSidebarCats ? '#e0f2fe' : '#f0f9ff',
                        border: '1.5px dashed #007da5',
                        color: '#007da5',
                        padding: '7px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        touchAction: 'manipulation'
                      }}
                    >
                      {showAllSidebarCats ? 'Më pak ▲' : `+ Më shumë (${categoriesList.length - 6})`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Globe Promo Card (Fully Editable) */}
            <div
              style={{
                background: 'linear-gradient(135deg, #007da5 0%, #005a77 100%)',
                borderRadius: 16,
                padding: 28,
                color: '#ffffff',
                boxShadow: '0 8px 24px rgba(0, 125, 165, 0.2)',
                position: 'relative'
              }}
            >
              {admin && (
                <button
                  type="button"
                  onClick={() => {
                    setTempPromoBadge(promoBadge);
                    setTempPromoTitle(promoTitle);
                    setTempPromoDesc(promoDescription);
                    setTempPromoBtnText(promoBtnText);
                    setTempPromoBtnLink(promoBtnLink);
                    setShowPromoModal(true);
                  }}
                  title="Ndrysho Reklamën"
                  style={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Edito
                </button>
              )}
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#f48d21', marginBottom: 6 }}>
                {promoBadge}
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px 0', lineHeight: 1.3 }}>
                {promoTitle}
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, margin: '0 0 18px 0' }}>
                {promoDescription}
              </p>
              {promoBtnLink?.startsWith('http') ? (
                <a
                  href={promoBtnLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-promo-action-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: '#f7931e',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: 13.5,
                    padding: '11px 22px',
                    borderRadius: 12,
                    textDecoration: 'none',
                    boxShadow: '0 4px 14px rgba(247, 147, 30, 0.32)',
                    transition: 'all 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    letterSpacing: '-0.2px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ea580c';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(247, 147, 30, 0.48)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7931e';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(247, 147, 30, 0.32)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.backgroundColor = '#d97706';
                    e.currentTarget.style.transform = 'scale(0.96) translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(247, 147, 30, 0.25)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '#ea580c';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(247, 147, 30, 0.48)';
                  }}
                >
                  <span>{promoBtnText}</span>
                </a>
              ) : (
                <Link
                  to={promoBtnLink || '/products'}
                  className="blog-promo-action-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: '#f7931e',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: 13.5,
                    padding: '11px 22px',
                    borderRadius: 12,
                    textDecoration: 'none',
                    boxShadow: '0 4px 14px rgba(247, 147, 30, 0.32)',
                    transition: 'all 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    letterSpacing: '-0.2px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ea580c';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(247, 147, 30, 0.48)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7931e';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(247, 147, 30, 0.32)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.backgroundColor = '#d97706';
                    e.currentTarget.style.transform = 'scale(0.96) translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(247, 147, 30, 0.25)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = '#ea580c';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(247, 147, 30, 0.48)';
                  }}
                >
                  <span>{promoBtnText}</span>
                </Link>
              )}
            </div>

          </aside>
        </div>

        {/* Bottom Related Articles Section */}
        {bottomRelated.length > 0 && (
          <section style={{ marginTop: 64, paddingTop: 40, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Mund t'ju interesojë gjithashtu
                </h2>
                <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0 0' }}>
                  Eksploroni më shumë artikuj dhe këshilla nga blogu ynë
                </p>
              </div>
              <Link
                to="/blog"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#007da5',
                  textDecoration: 'none'
                }}
              >
                Shiko të gjithë →
              </Link>
            </div>

            <div className="cards-wrapper">
              {bottomRelated.map(p => {
                const pImg = p.image ? resolveAssetUrl(p.image) : 'https://picsum.photos/seed/blog' + p.id + '/800/600';
                const pCats = getPostCategories(p);
                return (
                  <Link key={p.id} className="mac-blog-card" to={`/blog/${p.id}`}>
                    <div className="mac-blog-img-wrap">
                      <img src={pImg} alt={p.title} loading="lazy" />
                    </div>
                    <div className="mac-blog-content">
                      <div className="mac-blog-meta" style={{ flexWrap: 'wrap', gap: 6 }}>
                        {pCats.map(c => (
                          <span key={c} className="mac-blog-tag">{c}</span>
                        ))}
                        {p.createdAt && <span className="mac-blog-date">{new Date(p.createdAt).toLocaleDateString()}</span>}
                      </div>
                      <h3 className="mac-blog-title">{p.title}</h3>
                      <p className="mac-blog-excerpt">{p.excerpt || (p.content ? p.content.replace(/<[^>]+>/g, '').slice(0, 110) + '…' : '')}</p>
                      <div className="mac-blog-footer">
                        <span>Lexo artikullin</span>
                        <span>→</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

      </div>

      {/* Edit Promo Banner Modal */}
      {showPromoModal && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: 20, boxSizing: 'border-box' }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: 600, background: '#fff', padding: 24, borderRadius: 12, position: 'relative', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Edito Kartën Reklamuese të Sidebar-it</h3>
              <button
                type="button"
                onClick={() => setShowPromoModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSavePromo} style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Badge i sipërm (Teksti portokalli)</label>
                <input
                  type="text"
                  value={tempPromoBadge}
                  onChange={(e) => setTempPromoBadge(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Titulli i Kartës</label>
                <input
                  type="text"
                  value={tempPromoTitle}
                  onChange={(e) => setTempPromoTitle(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Përshkrimi</label>
                <textarea
                  rows={3}
                  value={tempPromoDesc}
                  onChange={(e) => setTempPromoDesc(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Teksti i Butonit</label>
                  <input
                    type="text"
                    value={tempPromoBtnText}
                    onChange={(e) => setTempPromoBtnText(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Linku i Butonit</label>
                  <input
                    type="text"
                    value={tempPromoBtnLink}
                    onChange={(e) => setTempPromoBtnLink(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowPromoModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  disabled={savingPromo}
                  style={{ padding: '8px 20px', background: '#007da5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  {savingPromo ? 'Duke ruajtur...' : 'Ruaj Reklamën'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
